/**
 * Rug pad upsell: mirrors the size chosen on the main product (the rug) into the rug pad
 * variant select. The shopper can still change the rug pad size manually afterwards.
 */
class RugpadUpsell extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.select = this.querySelector('select');
    if (!this.select) return;

    this.initialized = true;
    this.sizeMap = this.buildSizeMap();

    this.onDocumentChange = this.onDocumentChange.bind(this);
    document.addEventListener('change', this.onDocumentChange);

    this.syncWithMainVariant(this.mainVariantSelect);
  }

  disconnectedCallback() {
    if (this.onDocumentChange) document.removeEventListener('change', this.onDocumentChange);
  }

  get mainVariantSelect() {
    return document.querySelector('variant-picker select');
  }

  buildSizeMap() {
    const map = new Map();

    this.select.querySelectorAll('option[data-variant-title]').forEach(option => {
      const size = RugpadUpsell.normalizeSize(option.dataset.variantTitle);
      if (size && !map.has(size)) map.set(size, option.value);
    });

    return map;
  }

  onDocumentChange(event) {
    const target = event.target;

    if (target === this.select) {
      this.clearErrorMessage();
      return;
    }

    if (target instanceof HTMLSelectElement && target.closest('variant-picker')) {
      this.syncWithMainVariant(target);
    }
  }

  syncWithMainVariant(mainSelect) {
    // No rug size selected yet -> leave the rug pad select in its initial state.
    if (!mainSelect || !mainSelect.value || mainSelect.value === 'default') return;

    const selectedOption = mainSelect.options[mainSelect.selectedIndex];
    const size = RugpadUpsell.normalizeSize(selectedOption?.textContent);
    const matchedValue = size ? this.sizeMap.get(size) : null;

    if (this.select.value === (matchedValue || '')) return;

    this.select.value = matchedValue || '';
    this.select.dispatchEvent(new Event('change', { bubbles: true }));
  }

  clearErrorMessage() {
    const form = this.closest('product-form');
    if (!form) return;

    const errorWrapper = form.querySelector('.product-form__error-message-wrapper');
    const errorMessage = form.querySelector('.product-form__error-message');

    if (errorWrapper) errorWrapper.setAttribute('hidden', 'true');
    if (errorMessage) errorMessage.textContent = '';
  }

  /**
   * Reduces a variant title to a comparable size key, e.g.
   * "160 x 230 cm" -> "160x230", "Ø 200 cm" -> "d200".
   */
  static normalizeSize(title) {
    if (!title) return '';

    const value = title.toLowerCase().replace(/,/g, '.').replace(/\s+/g, ' ').trim();

    const pair = value.match(/(\d+(?:\.\d+)?)\s*[x×*]\s*(\d+(?:\.\d+)?)/);
    if (pair) return `${parseFloat(pair[1])}x${parseFloat(pair[2])}`;

    const round = value.match(/[ø⌀]\s*(\d+(?:\.\d+)?)/);
    if (round) return `d${parseFloat(round[1])}`;

    const single = value.match(/(\d+(?:\.\d+)?)/);
    if (single) return `${parseFloat(single[1])}`;

    return value;
  }
}

if (!customElements.get('rugpad-upsell')) {
  customElements.define('rugpad-upsell', RugpadUpsell);
}
